// Y2K / Acid Revival landing page — monolithic iframe block
// 4 brand variants: core / heat / vapor / void

export type Y2kLandingBrand = 'core' | 'heat' | 'vapor' | 'void'
export interface Y2kLandingPageData { brand: Y2kLandingBrand }
export const DEFAULT_Y2K_LANDING_DATA: Y2kLandingPageData = { brand: 'core' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const Y2K_LANDING_CSS = `
/* ============ BST WORLDWIDE / Y2K ACID ENGINE ============ */
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box}

:root{--ease:cubic-bezier(.22,1,.36,1)}

/* ---- VARIANT PALETTES ---- */
body[data-variant="core"]{
  --bg:#060a06;--panel:#0d130d;--panel2:#111a11;--fg:#e2ffe2;--mut:#8fb98f;
  --a1:#00ff41;--a2:#ff00e5;--a3:#f8ff00;--line:#274027;
  --glow:rgba(0,255,65,.35);--grid:rgba(0,255,65,.14);
}
body[data-variant="heat"]{
  --bg:#0d0502;--panel:#190b05;--panel2:#221007;--fg:#ffedda;--mut:#c78d67;
  --a1:#ff5e00;--a2:#ffd300;--a3:#ff0055;--line:#4a2412;
  --glow:rgba(255,94,0,.35);--grid:rgba(255,94,0,.14);
}
body[data-variant="vapor"]{
  --bg:#0c0418;--panel:#160a28;--panel2:#1e0e35;--fg:#f4ebff;--mut:#a98cc9;
  --a1:#ff71ce;--a2:#01cdfe;--a3:#b967ff;--line:#3a2158;
  --glow:rgba(255,113,206,.35);--grid:rgba(1,205,254,.15);
}
body[data-variant="void"]{
  --bg:#030312;--panel:#0a0a22;--panel2:#0e0e2e;--fg:#e2e8ff;--mut:#8b93c9;
  --a1:#00f0ff;--a2:#7b2bff;--a3:#ff3df2;--line:#232355;
  --glow:rgba(0,240,255,.32);--grid:rgba(0,240,255,.13);
}

html{scroll-behavior:smooth;scroll-padding-top:96px}
body{
  background:var(--bg);color:var(--fg);
  font-family:'IBM Plex Mono',monospace;font-size:15px;line-height:1.65;
  overflow-x:hidden;transition:background-color .5s,color .5s;
}
::selection{background:var(--a2);color:#000}
img{display:block;max-width:100%}
a{color:inherit;text-decoration:none}
button{font-family:inherit;cursor:pointer}
h1,h2,h3{font-family:'Unbounded',sans-serif;line-height:1.05}
.px{font-family:'Press Start 2P',monospace}
.wrap{width:min(1240px,92%);margin:0 auto}

/* ---- CRT / NOISE OVERLAYS ---- */
.crt{position:fixed;inset:0;z-index:120;pointer-events:none;
  background:repeating-linear-gradient(0deg,rgba(0,0,0,.22) 0 1px,transparent 1px 3px);
  mix-blend-mode:multiply}
.crt::after{content:"";position:absolute;left:0;right:0;height:120px;top:-140px;
  background:linear-gradient(180deg,transparent,rgba(255,255,255,.06),transparent);
  animation:scan 7s linear infinite}
@keyframes scan{to{transform:translateY(120vh)}}
.noise{position:fixed;inset:-50%;z-index:121;pointer-events:none;opacity:.05;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E");
  animation:noiseShift .45s steps(3) infinite}
@keyframes noiseShift{0%{transform:translate(0,0)}50%{transform:translate(2%, -1%)}100%{transform:translate(-1%,2%)}}

/* ---- FLASH ON VARIANT SWITCH ---- */
#flash{position:fixed;inset:0;z-index:200;background:#fff;opacity:0;pointer-events:none}
#flash.on{animation:flash .28s ease-out}
@keyframes flash{0%{opacity:.85}100%{opacity:0}}

/* ---- LOADER ---- */
#loader{position:fixed;inset:0;z-index:300;background:var(--bg);display:flex;flex-direction:column;gap:22px;align-items:center;justify-content:center;transition:opacity .5s,visibility .5s}
#loader.done{opacity:0;visibility:hidden}
#loader .l-logo{font-family:'Bungee';font-size:56px;letter-spacing:.05em;
  background:linear-gradient(180deg,#fff 0%,#d9d9d9 38%,#6f6f6f 50%,#fff 55%,#bdbdbd 70%,#7c7c7c 84%,#f2f2f2 100%);
  -webkit-background-clip:text;background-clip:text;color:transparent}
#loader .l-bar{width:min(320px,70vw);height:14px;border:2px solid var(--fg);padding:2px}
#loader .l-fill{height:100%;width:0;background:repeating-linear-gradient(90deg,var(--a1) 0 8px,var(--a2) 8px 16px)}
#loader .l-txt{font-size:10px;color:var(--a1)}

/* ---- HEADER ---- */
.hdr{position:fixed;top:0;left:0;right:0;z-index:150;border-bottom:2px solid var(--line);
  background:color-mix(in srgb,var(--bg) 82%,transparent);backdrop-filter:blur(8px)}
.hdr__in{width:min(1360px,94%);margin:0 auto;height:72px;display:flex;align-items:center;gap:26px}
.logo{display:flex;align-items:center;gap:12px;flex-shrink:0}
.emblem{width:40px;height:40px;display:grid;place-items:center;transition:transform .4s var(--ease)}
.logo:hover .emblem{transform:rotate(180deg)}
.emblem svg{width:100%;height:100%;display:none}
body[data-variant="core"] .em--core{display:block}
body[data-variant="heat"] .em--heat{display:block}
body[data-variant="vapor"] .em--vapor{display:block}
body[data-variant="void"] .em--void{display:block}
.logo__txt{font-family:'Bungee';font-size:26px;letter-spacing:.03em;
  background:linear-gradient(180deg,#fff,#cfcfcf 42%,#787878 50%,#fff 56%,#a8a8a8 78%,#e8e8e8);
  -webkit-background-clip:text;background-clip:text;color:transparent}
.logo__txt sup{font-size:10px;color:var(--a1);-webkit-text-fill-color:var(--a1)}
.nav{display:flex;gap:4px;margin-left:auto}
.nav a{font-family:'Press Start 2P';font-size:10px;padding:10px 14px;color:var(--mut);position:relative;transition:color .2s}
.nav a::after{content:"";position:absolute;left:14px;right:14px;bottom:5px;height:3px;background:var(--a1);transform:scaleX(0);transform-origin:left;transition:transform .25s var(--ease)}
.nav a:hover{color:var(--fg)}
.nav a:hover::after{transform:scaleX(1)}
.hdr__right{display:flex;align-items:center;gap:14px;margin-left:auto}
.nav + .hdr__right{margin-left:0}
.switcher{display:flex;gap:8px;padding:8px 10px;border:2px solid var(--line);background:var(--panel)}
.sw{width:20px;height:20px;border:2px solid #000;box-shadow:0 0 0 2px var(--line);transition:transform .2s,box-shadow .2s}
.sw:hover{transform:translateY(-3px)}
.sw.is-on{box-shadow:0 0 0 2px var(--fg);transform:scale(1.1)}
.sw--core{background:#00ff41}.sw--heat{background:#ff5e00}.sw--vapor{background:#ff71ce}.sw--void{background:#00f0ff}
.burger{display:none;width:44px;height:44px;border:2px solid var(--fg);background:var(--a1);color:#000;font-size:20px;font-weight:600;box-shadow:4px 4px 0 var(--fg)}

/* ---- MOBILE MENU ---- */
.menu{position:fixed;inset:0;z-index:180;background:var(--bg);transform:translateY(-102%);transition:transform .5s var(--ease);display:flex;flex-direction:column;justify-content:center;padding:100px 8% 40px;
  background-image:linear-gradient(var(--grid) 1px,transparent 1px),linear-gradient(90deg,var(--grid) 1px,transparent 1px);background-size:44px 44px}
body.menu-open .menu{transform:translateY(0)}
.menu a.big{font-family:'Unbounded';font-weight:900;font-size:clamp(28px,8vw,52px);padding:10px 0;border-bottom:2px dashed var(--line);display:flex;justify-content:space-between;align-items:center}
.menu a.big span{font-family:'Press Start 2P';font-size:10px;color:var(--a1)}
.menu a.big:hover{color:var(--a1)}
.menu .switcher{margin-top:34px;align-self:flex-start}

/* ---- HERO ---- */
.hero{position:relative;min-height:100svh;display:flex;align-items:center;padding:130px 0 90px;overflow:hidden;
  background-image:linear-gradient(var(--grid) 1px,transparent 1px),linear-gradient(90deg,var(--grid) 1px,transparent 1px);
  background-size:52px 52px}
.hero::before{content:"";position:absolute;inset:0;background:radial-gradient(600px 420px at 78% 40%,var(--glow),transparent 70%)}
.hero__in{width:min(1360px,94%);margin:0 auto;display:grid;grid-template-columns:1.15fr .85fr;gap:40px;align-items:center;position:relative}
.pixel-tag{font-family:'Press Start 2P';font-size:clamp(9px,1.2vw,12px);color:var(--a1);margin-bottom:22px}
.pixel-tag b{color:var(--a2)}
.hero__title{position:relative;font-family:'Bungee';font-size:clamp(96px,21vw,300px);line-height:.9;letter-spacing:.01em;
  background:linear-gradient(180deg,#fff 0%,#e6e6e6 34%,#8c8c8c 49%,#ffffff 54%,#c9c9c9 68%,#767676 82%,#f7f7f7 100%);
  -webkit-background-clip:text;background-clip:text;color:transparent;
  filter:drop-shadow(4px 4px 0 var(--a2)) drop-shadow(8px 8px 0 rgba(0,0,0,.6))}
.hero__title::before,.hero__title::after{content:attr(data-text);position:absolute;inset:0;background:none;-webkit-text-fill-color:currentColor;opacity:0;pointer-events:none}
.hero__title::before{color:var(--a1)}
.hero__title::after{color:var(--a2)}
.hero__title.glitching::before{opacity:.85;animation:glA .3s steps(2) both}
.hero__title.glitching::after{opacity:.85;animation:glB .3s steps(2) both}
@keyframes glA{0%{clip-path:inset(0 0 78% 0);transform:translate(-8px,-4px)}50%{clip-path:inset(28% 0 44% 0);transform:translate(7px,3px)}100%{clip-path:inset(60% 0 8% 0);transform:translate(-4px,0)}}
@keyframes glB{0%{clip-path:inset(72% 0 0 0);transform:translate(8px,4px)}50%{clip-path:inset(40% 0 34% 0);transform:translate(-7px,-3px)}100%{clip-path:inset(6% 0 66% 0);transform:translate(4px,0)}}
.hero__tag{font-family:'Press Start 2P';font-size:clamp(10px,1.4vw,15px);color:var(--a1);margin:26px 0 14px;letter-spacing:.06em}
.hero__tag em{font-style:normal;color:var(--a3)}
.hero__sub{max-width:520px;color:var(--mut);font-size:15px}
.hero__cta{display:flex;gap:18px;flex-wrap:wrap;margin-top:34px}
.btn{display:inline-flex;align-items:center;gap:10px;font-family:'Press Start 2P';font-size:11px;padding:18px 24px;border:3px solid var(--fg);background:var(--a1);color:#000;box-shadow:6px 6px 0 var(--fg);transition:transform .15s,box-shadow .15s,background .3s}
.btn:hover{transform:translate(-3px,-3px);box-shadow:9px 9px 0 var(--fg)}
.btn:active{transform:translate(4px,4px);box-shadow:1px 1px 0 var(--fg)}
.btn--ghost{background:transparent;color:var(--fg);box-shadow:6px 6px 0 var(--a2)}
.btn--ghost:hover{box-shadow:9px 9px 0 var(--a2);background:var(--a2);color:#000}
.hero__meta{margin-top:44px;display:flex;gap:26px;flex-wrap:wrap;font-family:'Press Start 2P';font-size:9px;color:var(--mut)}
.hero__meta b{color:var(--a2)}
.hero__right{position:relative;display:grid;place-items:center;min-height:420px}
.hero__orb{width:min(420px,70%);border:4px solid var(--fg);box-shadow:10px 10px 0 var(--a2),10px 10px 0 4px #000;animation:floaty 6s ease-in-out infinite;image-rendering:auto}
@keyframes floaty{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-22px) rotate(1.5deg)}}
.rotbadge{position:absolute;width:150px;height:150px;left:2%;bottom:4%;animation:spin 14s linear infinite}
.rotbadge text{fill:var(--fg);font-family:'Press Start 2P';font-size:10.5px;letter-spacing:2px}
.rotbadge .rb-core{font-family:'Bungee';font-size:26px;fill:var(--a1)}
@keyframes spin{to{transform:rotate(360deg)}}
.sticker{position:absolute;font-family:'Press Start 2P';font-size:10px;padding:10px 14px;border:3px solid #000;background:var(--a3);color:#000;box-shadow:5px 5px 0 #000;transform:rotate(8deg)}
.sticker--star{top:2%;right:6%;background:var(--a2);transform:rotate(-7deg);animation:floaty 5s ease-in-out infinite}
.sticker--acid{bottom:14%;right:0;transform:rotate(5deg)}
.hero__scroll{position:absolute;bottom:22px;left:50%;transform:translateX(-50%);font-family:'Press Start 2P';font-size:9px;color:var(--mut);animation:blink 1.2s steps(2) infinite}
@keyframes blink{50%{opacity:.15}}

/* ---- TICKER ---- */
.ticker{border-top:3px solid var(--fg);border-bottom:3px solid var(--fg);background:var(--a1);color:#000;overflow:hidden;transform:rotate(-1.2deg) scale(1.02)}
.ticker--rev{background:var(--a2);transform:rotate(1.2deg) scale(1.02)}
.ticker__track{display:flex;width:max-content;animation:tick 20s linear infinite}
.ticker span{font-family:'Bungee';font-size:clamp(16px,2.4vw,26px);padding:12px 0;white-space:nowrap}
@keyframes tick{to{transform:translateX(-50%)}}

/* ---- SECTIONS COMMON ---- */
section{padding:110px 0}
.sec-head{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;margin-bottom:56px;flex-wrap:wrap}
.sec-label{font-family:'Press Start 2P';font-size:10px;color:var(--a2);display:block;margin-bottom:16px}
.sec-title{font-weight:900;font-size:clamp(30px,5.4vw,64px);text-transform:uppercase}
.sec-title i{font-style:normal;color:var(--a1)}
.sec-note{font-family:'Press Start 2P';font-size:9px;color:var(--mut);max-width:260px;text-align:right}

/* ---- MANIFESTO ---- */
.mani{position:relative}
.mani__grid{display:grid;grid-template-columns:1.2fr .8fr;gap:60px;align-items:start}
.mani__text{font-family:'Unbounded';font-weight:700;font-size:clamp(18px,2.6vw,30px);line-height:1.5}
.mani__text mark{background:var(--a1);color:#000;padding:0 8px;box-decoration-break:clone;-webkit-box-decoration-break:clone}
.mani__text mark.m2{background:var(--a2)}
.mani__text mark.m3{background:var(--a3)}
.mani__side{position:relative}
.mani__side img{border:4px solid var(--fg);box-shadow:10px 10px 0 var(--a1);transform:rotate(2deg)}
.mani__list{margin-top:34px;display:grid;gap:0}
.mani__list li{list-style:none;display:flex;gap:14px;align-items:center;padding:14px 4px;border-bottom:2px dashed var(--line);font-family:'Press Start 2P';font-size:10px;color:var(--mut)}
.mani__list li b{color:var(--a1)}
.mani__list li:hover{color:var(--fg);background:var(--panel)}

/* ---- SERVICES ---- */
.srv-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:22px}
.srv{border:3px solid var(--fg);background:var(--panel);padding:26px 22px 30px;position:relative;box-shadow:8px 8px 0 var(--line);transition:transform .25s var(--ease),box-shadow .25s,background .3s}
.srv:hover{transform:translate(-5px,-5px);box-shadow:13px 13px 0 var(--a1);background:var(--panel2)}
.srv__idx{font-family:'Press Start 2P';font-size:10px;color:var(--a2)}
.srv__ico{font-size:34px;color:var(--a1);margin:18px 0 14px;display:block;text-shadow:3px 3px 0 var(--a2)}
.srv h3{font-size:17px;font-weight:700;text-transform:uppercase;margin-bottom:12px}
.srv p{font-size:13px;color:var(--mut)}
.srv__tags{margin-top:18px;display:flex;flex-wrap:wrap;gap:6px}
.srv__tags span{font-family:'Press Start 2P';font-size:8px;border:2px solid var(--line);padding:5px 8px;color:var(--mut)}
.srv:hover .srv__tags span{border-color:var(--a1);color:var(--a1)}

/* ---- WORKS ---- */
.works-grid{display:grid;grid-template-columns:1fr 1fr;gap:34px}
.work{border:3px solid var(--fg);background:var(--panel);box-shadow:10px 10px 0 var(--a2);transition:transform .3s var(--ease),box-shadow .3s}
.work:nth-child(even){box-shadow:10px 10px 0 var(--a1)}
.work:hover{transform:translate(-6px,-6px) rotate(-.5deg);box-shadow:16px 16px 0 var(--a3)}
.work__img{position:relative;overflow:hidden;border-bottom:3px solid var(--fg);aspect-ratio:4/3}
.work__img img{width:100%;height:100%;object-fit:cover;transition:transform .6s var(--ease),filter .4s;filter:saturate(1.15)}
.work:hover .work__img img{transform:scale(1.07) rotate(1deg);filter:saturate(1.5) contrast(1.1)}
.work__tag{position:absolute;top:14px;left:14px;font-family:'Press Start 2P';font-size:9px;background:var(--a3);color:#000;border:3px solid #000;padding:7px 10px;box-shadow:4px 4px 0 #000}
.work__info{display:flex;align-items:center;gap:14px;padding:20px 22px}
.work__info h3{font-size:clamp(15px,1.8vw,20px);font-weight:700}
.work__info p{font-size:12px;color:var(--mut)}
.work__year{margin-left:auto;font-family:'Press Start 2P';font-size:10px;color:var(--a1)}

/* ---- STATS ---- */
.stats{background:var(--panel);border-top:3px solid var(--fg);border-bottom:3px solid var(--fg)}
.stats__grid{display:grid;grid-template-columns:repeat(4,1fr);gap:20px;text-align:center}
.stat{padding:26px 10px;border-right:2px dashed var(--line)}
.stat:last-child{border-right:none}
.stat__num{font-family:'Bungee';font-size:clamp(38px,5.5vw,72px);color:var(--a1);text-shadow:4px 4px 0 var(--a2)}
.stat p{font-family:'Press Start 2P';font-size:9px;color:var(--mut);margin-top:12px}

/* ---- PROCESS ---- */
.proc-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:22px;counter-reset:st}
.proc{position:relative;padding:26px 20px;border:3px dashed var(--line);transition:border-color .3s,transform .3s var(--ease)}
.proc:hover{border-color:var(--a1);transform:translateY(-6px)}
.proc__n{font-family:'Bungee';font-size:52px;color:transparent;-webkit-text-stroke:2px var(--a1)}
.proc h3{font-size:16px;margin:14px 0 10px;text-transform:uppercase}
.proc h3 i{font-style:normal;color:var(--a2)}
.proc p{font-size:13px;color:var(--mut)}
.proc::after{content:"▶";position:absolute;top:26px;right:18px;color:var(--a2);animation:blink 1s steps(2) infinite}

/* ---- CONTACT ---- */
.cta{position:relative;overflow:hidden}
.cta::before{content:"";position:absolute;inset:0;background:radial-gradient(700px 400px at 20% 20%,var(--glow),transparent 70%)}
.cta__grid{display:grid;grid-template-columns:1fr 1fr;gap:60px;position:relative}
.cta__title{font-weight:900;font-size:clamp(32px,5vw,60px);text-transform:uppercase}
.cta__title i{font-style:normal;color:var(--a2)}
.cta__links{margin-top:34px;display:grid;gap:14px}
.cta__links a{font-family:'Press Start 2P';font-size:12px;color:var(--a1);width:max-content;border-bottom:3px solid var(--a1);padding-bottom:4px;transition:color .2s,border-color .2s,transform .2s}
.cta__links a:hover{color:var(--a3);border-color:var(--a3);transform:translateX(6px)}
.form{display:grid;gap:18px}
.form label{font-family:'Press Start 2P';font-size:9px;color:var(--mut);display:block;margin-bottom:8px}
.form input,.form textarea{width:100%;background:var(--panel);border:3px solid var(--fg);color:var(--fg);font-family:'IBM Plex Mono',monospace;font-size:14px;padding:14px;outline:none;transition:box-shadow .2s,border-color .2s}
.form input:focus,.form textarea:focus{border-color:var(--a1);box-shadow:6px 6px 0 var(--a1)}
.form textarea{min-height:110px;resize:vertical}
.budgets{display:flex;gap:10px;flex-wrap:wrap}
.budgets input{display:none}
.budgets span{display:inline-block;font-family:'Press Start 2P';font-size:9px;padding:10px 14px;border:2px solid var(--line);color:var(--mut);cursor:pointer;transition:all .2s}
.budgets input:checked + span{background:var(--a2);color:#000;border-color:var(--fg);box-shadow:4px 4px 0 var(--fg)}
.form-ok{display:none;border:3px solid var(--a1);background:var(--panel);padding:26px;font-family:'Press Start 2P';font-size:11px;color:var(--a1);text-align:center}
.form-ok.show{display:block;animation:okPop .4s var(--ease)}
@keyframes okPop{from{transform:scale(.8);opacity:0}}

/* ---- FOOTER ---- */
footer{border-top:3px solid var(--fg);padding:70px 0 30px;overflow:hidden}
.foot__giant{font-family:'Bungee';font-size:clamp(90px,22vw,340px);line-height:.85;text-align:center;color:transparent;-webkit-text-stroke:3px var(--a1);text-shadow:8px 8px 0 var(--a2);transition:color .4s;user-select:none}
.foot__giant:hover{color:var(--a1)}
.foot__row{display:flex;justify-content:space-between;gap:20px;flex-wrap:wrap;margin-top:56px;font-family:'Press Start 2P';font-size:9px;color:var(--mut)}
.foot__row a:hover{color:var(--a1)}
.foot__soc{display:flex;gap:16px}

/* ---- REVEAL ---- */
.rv{opacity:0;transform:translateY(46px)}
.rv.in{opacity:1;transform:none;transition:opacity .8s var(--ease),transform .8s var(--ease)}

/* ---- SPARK CURSOR ---- */
.spark{position:fixed;z-index:210;pointer-events:none;font-size:14px;color:var(--a1);animation:sparkFade .7s forwards}
@keyframes sparkFade{to{transform:translateY(-26px) rotate(120deg) scale(.2);opacity:0}}

/* ============ RESPONSIVE ============ */
@media(max-width:1024px){
  .srv-grid{grid-template-columns:repeat(2,1fr)}
  .proc-grid{grid-template-columns:repeat(2,1fr)}
  .stats__grid{grid-template-columns:repeat(2,1fr)}
  .stat:nth-child(2){border-right:none}
  .stat{border-bottom:2px dashed var(--line)}
  .stat:nth-child(n+3){border-bottom:none}
  .hero__in{grid-template-columns:1fr;gap:60px}
  .hero__right{min-height:auto}
  .mani__grid{grid-template-columns:1fr}
  .cta__grid{grid-template-columns:1fr;gap:44px}
}
@media(max-width:760px){
  .nav{display:none}
  .burger{display:block}
  section{padding:80px 0}
  .works-grid{grid-template-columns:1fr}
  .sec-note{display:none}
  .hdr__in{height:64px;gap:14px}
  .switcher{padding:6px 8px;gap:6px}
  .sw{width:16px;height:16px}
  .hero{padding-top:110px}
  .rotbadge{width:110px;height:110px}
  .sticker--star{display:none}
}
@media(max-width:520px){
  .srv-grid{grid-template-columns:1fr}
  .proc-grid{grid-template-columns:1fr}
  .stats__grid{grid-template-columns:1fr}
  .stat{border-right:none;border-bottom:2px dashed var(--line)}
  .stat:last-child{border-bottom:none}
  .hero__cta .btn{width:100%;justify-content:center}
  .logo__txt{font-size:20px}
}
@media(prefers-reduced-motion:reduce){
  *,*::before,*::after{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const Y2K_LANDING_HTML_BODY = `
<div class="crt" aria-hidden="true"></div>
<div class="noise" aria-hidden="true"></div>
<div id="flash" aria-hidden="true"></div>

<!-- LOADER -->
<div id="loader">
  <div class="l-logo">BST</div>
  <div class="l-bar"><div class="l-fill" id="lFill"></div></div>
  <div class="l-txt px">ЗАГРУЗКА КИСЛОТЫ… <span id="lPct">0</span>%</div>
</div>

<!-- HEADER -->
<header class="hdr">
  <div class="hdr__in">
    <a class="logo" href="#top" aria-label="BST home">
      <span class="emblem">
        <svg class="em--core" viewBox="0 0 48 48"><circle cx="24" cy="24" r="21" fill="none" stroke="var(--a1)" stroke-width="3" stroke-dasharray="5 4"/><circle cx="24" cy="24" r="15" fill="var(--a1)"/><path d="M27 10 L16 27 h7 l-3 11 12-18 h-7 z" fill="#000"/></svg>
        <svg class="em--heat" viewBox="0 0 48 48"><path d="M24 3c2 8 11 10 11 20a11 11 0 0 1-22 0c0-6 4-8 4-14 3 2 6 4 7-6z" fill="var(--a1)"/><path d="M24 21c1 4 5 4 5 8a5 5 0 0 1-10 0c0-4 4-4 5-8z" fill="var(--a2)"/></svg>
        <svg class="em--vapor" viewBox="0 0 48 48"><defs><clipPath id="sunc"><circle cx="24" cy="24" r="17"/></clipPath></defs><circle cx="24" cy="24" r="21" fill="none" stroke="var(--a2)" stroke-width="2" stroke-dasharray="2 4"/><g clip-path="url(#sunc)"><circle cx="24" cy="24" r="17" fill="var(--a1)"/><rect x="4" y="25" width="40" height="3" fill="var(--bg)"/><rect x="4" y="31" width="40" height="4" fill="var(--bg)"/><rect x="4" y="38" width="40" height="5" fill="var(--bg)"/></g></svg>
        <svg class="em--void" viewBox="0 0 48 48"><circle cx="24" cy="24" r="11" fill="var(--a2)"/><ellipse cx="24" cy="24" rx="21" ry="7" fill="none" stroke="var(--a1)" stroke-width="2.5" transform="rotate(-18 24 24)"/><circle cx="41" cy="16" r="2.5" fill="var(--a3)"/></svg>
      </span>
      <span class="logo__txt">BST<sup>®</sup></span>
    </a>
    <nav class="nav">
      <a href="#manifesto">МАНИФЕСТ</a>
      <a href="#services">УСЛУГИ</a>
      <a href="#works">РАБОТЫ</a>
      <a href="#contact">КОНТАКТ</a>
    </nav>
    <div class="hdr__right">
      <div class="switcher" role="group" aria-label="Вариант бренда">
        <button class="sw sw--core is-on" data-v="core" title="BST CORE"></button>
        <button class="sw sw--heat" data-v="heat" title="BST HEAT"></button>
        <button class="sw sw--vapor" data-v="vapor" title="BST VAPOR"></button>
        <button class="sw sw--void" data-v="void" title="BST VOID"></button>
      </div>
      <button class="burger" id="burger" aria-label="Меню">≡</button>
    </div>
  </div>
</header>

<!-- MOBILE MENU -->
<div class="menu" id="menu">
  <a class="big" href="#manifesto">МАНИФЕСТ <span>01</span></a>
  <a class="big" href="#services">УСЛУГИ <span>02</span></a>
  <a class="big" href="#works">РАБОТЫ <span>03</span></a>
  <a class="big" href="#contact">КОНТАКТ <span>04</span></a>
  <div class="switcher">
    <button class="sw sw--core is-on" data-v="core" title="BST CORE"></button>
    <button class="sw sw--heat" data-v="heat" title="BST HEAT"></button>
    <button class="sw sw--vapor" data-v="vapor" title="BST VAPOR"></button>
    <button class="sw sw--void" data-v="void" title="BST VOID"></button>
  </div>
</div>

<main id="top">

  <!-- HERO -->
  <section class="hero">
    <div class="hero__in">
      <div class="hero__left">
        <p class="pixel-tag">[ Y2K REVIVAL PROTOCOL <b>v4.0</b> ] // СИГНАЛ СТАБИЛЬНЫЙ</p>
        <h1 class="hero__title" id="heroTitle" data-text="BST">BST</h1>
        <p class="hero__tag" id="heroTag">★ DIGITAL HARDCORE <em>STUDIO</em> ★</p>
        <p class="hero__sub" id="heroSub">Хром, кислота и пиксели. Мы собираем бренды и сайты, которые невозможно пролистать — интернет снова должен быть диким.</p>
        <div class="hero__cta">
          <a class="btn" href="#works">СМОТРЕТЬ РАБОТЫ ★</a>
          <a class="btn btn--ghost" href="#contact">ЗАПУСТИТЬ ПРОЕКТ</a>
        </div>
        <div class="hero__meta">
          <span>РЕЖИМ: <b id="vName">BST CORE</b></span>
          <span>EST. <b>2025</b></span>
          <span>КИСЛОТА: <b>100%</b></span>
        </div>
      </div>
      <div class="hero__right">
        <img class="hero__orb" src="https://image.qwenlm.ai/public_source/de6aeb82-5a79-4489-912a-4c6ef79d9d71/1c5362e2f-2e16-43eb-bbe9-5bb3f599af5e.png" alt="BST chrome orb">
        <div class="sticker sticker--star">100% ACID ✦</div>
        <div class="sticker sticker--acid">NO BEIGE. EVER.</div>
        <svg class="rotbadge" viewBox="0 0 120 120" aria-hidden="true">
          <defs><path id="circ" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0"/></defs>
          <text><textPath href="#circ">★ BST WORLDWIDE ★ DIGITAL HARDCORE ★ EST 2025 </textPath></text>
          <text x="60" y="69" text-anchor="middle" class="rb-core">BST</text>
        </svg>
      </div>
    </div>
    <div class="hero__scroll">▼ SCROLL ▼</div>
  </section>

  <!-- TICKER -->
  <div class="ticker" aria-hidden="true"><div class="ticker__track">
    <span>&nbsp;★ BST ★ Y2K REVIVAL ★ ХРОМ ★ КИСЛОТА ★ БРЕНДИНГ ★ WEB ★ МОУШН ★ 3D ★ ПИКСЕЛИ ★ BST ★ Y2K REVIVAL ★ ХРОМ ★ КИСЛОТА ★ БРЕНДИНГ ★ WEB ★ МОУШН ★ 3D ★ ПИКСЕЛИ</span>
    <span>&nbsp;★ BST ★ Y2K REVIVAL ★ ХРОМ ★ КИСЛОТА ★ БРЕНДИНГ ★ WEB ★ МОУШН ★ 3D ★ ПИКСЕЛИ ★ BST ★ Y2K REVIVAL ★ ХРОМ ★ КИСЛОТА ★ БРЕНДИНГ ★ WEB ★ МОУШН ★ 3D ★ ПИКСЕЛИ</span>
  </div></div>

  <!-- MANIFESTO -->
  <section class="mani" id="manifesto">
    <div class="wrap">
      <div class="sec-head rv">
        <div><span class="sec-label">[ 01 / МАНИФЕСТ ]</span>
        <h2 class="sec-title">МЫ ПРОТИВ <i>СКУЧНОГО</i> ИНТЕРНЕТА</h2></div>
        <p class="sec-note">// читается под кислотный техно, громкость на максимум</p>
      </div>
      <div class="mani__grid">
        <p class="mani__text rv" data-t="maniText">Мы — <mark>BST</mark>. Мы верим, что сеть снова может быть дикой: <mark class="m2">хром</mark> вместо минимализма, <mark class="m3">кислота</mark> вместо бежевого, пиксели вместо стока. Мы делаем бренды, которые кричат, сайты, которые танцуют, и интерфейсы, которые невозможно забыть.</p>
        <div class="mani__side rv">
          <img data-img="maniImg" src="https://image.qwenlm.ai/public_source/de6aeb82-5a79-4489-912a-4c6ef79d9d71/13809642b-91ae-4f77-bda4-e33489e2e8cb.png" alt="acid smiley">
          <ul class="mani__list">
            <li><b>▶</b> СМЕРТЬ ШАБЛОНАМ</li>
            <li><b>▶</b> ХРОМ ДЛЯ ВСЕХ</li>
            <li><b>▶</b> ПИКСЕЛЬ — НОВЫЙ СЕРИФ</li>
            <li><b>▶</b> 60 FPS ИЛИ НИЧЕГО</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <!-- SERVICES -->
  <section id="services">
    <div class="wrap">
      <div class="sec-head rv">
        <div><span class="sec-label">[ 02 / УСЛУГИ ]</span>
        <h2 class="sec-title">ЧЕМ МЫ <i>ВЗРЫВАЕМ</i> СЕТЬ</h2></div>
        <p class="sec-note">// полный цикл: от искры до релиза</p>
      </div>
      <div class="srv-grid">
        <article class="srv rv"><span class="srv__idx">/01</span><span class="srv__ico">✦</span><h3 data-t="svc1Title">Брендинг</h3><p data-t="svc1Desc">Айдентики, которые хочется татуировать. Логотип, гайдлайн, характер.</p><div class="srv__tags"><span>ЛОГО</span><span>ГАЙДЛАЙН</span><span>НЕЙМ</span></div></article>
        <article class="srv rv"><span class="srv__idx">/02</span><span class="srv__ico">◉</span><h3 data-t="svc2Title">Веб-дизайн</h3><p data-t="svc2Desc">Сайты, за которые дают награды, а пользователи — скриншоты друзьям.</p><div class="srv__tags"><span>LENDED</span><span>E-COMM</span><span>WEBGL</span></div></article>
        <article class="srv rv"><span class="srv__idx">/03</span><span class="srv__ico">▲</span><h3 data-t="svc3Title">Моушн</h3><p data-t="svc3Desc">Заставки, рилсы, титры. Движение — это смысл, а не украшение.</p><div class="srv__tags"><span>2D/3D</span><span>РИЛСЫ</span><span>ТИТРЫ</span></div></article>
        <article class="srv rv"><span class="srv__idx">/04</span><span class="srv__ico">✖</span><h3 data-t="svc4Title">3D / CGI</h3><p data-t="svc4Desc">Хром, жидкость, металл. Рендерим будущее из прошлого.</p><div class="srv__tags"><span>РЕНДЕР</span><span>AR</span><span>МЕРЧ</span></div></article>
      </div>
    </div>
  </section>

  <!-- WORKS -->
  <section id="works">
    <div class="wrap">
      <div class="sec-head rv">
        <div><span class="sec-label">[ 03 / РАБОТЫ ]</span>
        <h2 class="sec-title">ДОКАЗАТЕЛЬСТВА <i>ПЕРЕГРУЗКИ</i></h2></div>
        <p class="sec-note">// каждый проект — маленький скандал</p>
      </div>
      <div class="works-grid">
        <article class="work rv">
          <div class="work__img"><img data-img="p1img" src="https://image.qwenlm.ai/public_source/de6aeb82-5a79-4489-912a-4c6ef79d9d71/1d3806c8c-f1c3-4768-8842-1c5dafa4c5af.png" alt="Chrome Heart Club"><span class="work__tag" data-t="p1Tag">АЙДЕНТИКА</span></div>
          <div class="work__info"><div><h3 data-t="p1Title">CHROME HEART CLUB</h3><p data-t="p1Desc">Брендинг / веб для закрытого клуба</p></div><span class="work__year">2025</span></div>
        </article>
        <article class="work rv">
          <div class="work__img"><img data-img="p2img" src="https://image.qwenlm.ai/public_source/de6aeb82-5a79-4489-912a-4c6ef79d9d71/110c538a5-b2ff-4bc3-afba-a672e40316a1.png" alt="Dolphin Dreams"><span class="work__tag" data-t="p2Tag">E-COMM</span></div>
          <div class="work__info"><div><h3 data-t="p2Title">DOLPHIN DREAMS</h3><p data-t="p2Desc">Магазин винтажных футболок</p></div><span class="work__year">2024</span></div>
        </article>
        <article class="work rv">
          <div class="work__img"><img data-img="p3img" src="https://image.qwenlm.ai/public_source/de6aeb82-5a79-4489-912a-4c6ef79d9d71/14dd1888c-ee93-475a-b14d-d8d196598e66.png" alt="Voice Page"><span class="work__tag" data-t="p3Tag">БРЕНДИНГ</span></div>
          <div class="work__info"><div><h3 data-t="p3Title">VOICE PAGE</h3><p data-t="p3Desc">Айдентика пиратского радио</p></div><span class="work__year">2025</span></div>
        </article>
        <article class="work rv">
          <div class="work__img"><img data-img="p4img" src="https://image.qwenlm.ai/public_source/de6aeb82-5a79-4489-912a-4c6ef79d9d71/155153f2a-9412-4bed-a5af-eaf2c8cd7fed.png" alt="Void Orbit"><span class="work__tag" data-t="p4Tag">WEB / 3D</span></div>
          <div class="work__info"><div><h3 data-t="p4Title">VOID ORBIT</h3><p data-t="p4Desc">Сайт фестиваля электронной музыки</p></div><span class="work__year">2024</span></div>
        </article>
      </div>
    </div>
  </section>

  <!-- STATS -->
  <div class="stats">
    <div class="wrap stats__grid">
      <div class="stat rv"><span class="stat__num" data-count="150">0</span><p data-t="stat1L">ПРОЕКТОВ В 12 СТРАНАХ</p></div>
      <div class="stat rv"><span class="stat__num" data-count="27">0</span><p data-t="stat2L">НАГРАД И ШОРТ-ЛИСТОВ</p></div>
      <div class="stat rv"><span class="stat__num" data-count="6">0</span><p data-t="stat3L">ЛЕТ ВАРИМ ХРОМ</p></div>
      <div class="stat rv"><span class="stat__num" data-count="9999">0</span><p data-t="stat4L">% КОНЦЕНТРАЦИЯ КИСЛОТЫ</p></div>
    </div>
  </div>

  <!-- PROCESS -->
  <section id="process">
    <div class="wrap">
      <div class="sec-head rv">
        <div><span class="sec-label">[ 04 / ПРОЦЕСС ]</span>
        <h2 class="sec-title">РЕАКЦИЯ ЗА <i>4 ТАКТА</i></h2></div>
      </div>
      <div class="proc-grid">
        <div class="proc rv"><span class="proc__n">01</span><h3><span data-t="step1Title">БРИФ</span> <i>//</i></h3><p data-t="step1Desc">Ловим сигнал: цели, аудитория, страхи и амбиции бренда.</p></div>
        <div class="proc rv"><span class="proc__n">02</span><h3><span data-t="step2Title">КИСЛОТА</span> <i>//</i></h3><p data-t="step2Desc">Растворяем скучное. Концепции, мудборды, направления.</p></div>
        <div class="proc rv"><span class="proc__n">03</span><h3><span data-t="step3Title">ХРОМ</span> <i>//</i></h3><p data-t="step3Desc">Полируем до блеска: дизайн, моушн, код, 3D.</p></div>
        <div class="proc rv"><span class="proc__n">04</span><h3><span data-t="step4Title">ЗАПУСК</span> <i>//</i></h3><p data-t="step4Desc">Взрываем интернет и следим за осколками аналитики.</p></div>
      </div>
    </div>
  </section>

  <!-- TICKER 2 -->
  <div class="ticker ticker--rev" aria-hidden="true"><div class="ticker__track">
    <span>&nbsp;✦ ГОТОВЫ К ПЕРЕГРУЗКЕ ✦ BST ✦ BST ✦ BST ✦ ГОТОВЫ К ПЕРЕГРУЗКЕ ✦ BST ✦ BST ✦ BST ✦ ГОТОВЫ К ПЕРЕГРУЗКЕ ✦ BST ✦ BST ✦ BST</span>
    <span>&nbsp;✦ ГОТОВЫ К ПЕРЕГРУЗКЕ ✦ BST ✦ BST ✦ BST ✦ ГОТОВЫ К ПЕРЕГРУЗКЕ ✦ BST ✦ BST ✦ BST ✦ ГОТОВЫ К ПЕРЕГРУЗКЕ ✦ BST ✦ BST ✦ BST</span>
  </div></div>

  <!-- CONTACT -->
  <section class="cta" id="contact">
    <div class="wrap cta__grid">
      <div class="rv">
        <span class="sec-label">[ 05 / КОНТАКТ ]</span>
        <h2 class="cta__title" data-t="ctaTitle">ГОТОВЫ К <i>ПЕРЕГРУЗКЕ?</i></h2>
        <p data-t="ctaDesc" style="color:var(--mut);margin-top:20px;max-width:420px">Расскажите о проекте — вернёмся с концепцией, сметой и парой кислотных референсов.</p>
        <div class="cta__links">
          <a href="mailto:hello@bst.world">HELLO@BST.WORLD</a>
          <a href="#">TG: @BST_WORLDWIDE</a>
        </div>
      </div>
      <form class="form rv" id="form">
        <div><label for="fName">ВАШЕ ИМЯ</label><input id="fName" required placeholder="НЕО ИЗ МАТРИЦЫ"></div>
        <div><label for="fMail">EMAIL</label><input id="fMail" type="email" required placeholder="neo@matrix.net"></div>
        <div><label for="fMsg">О ПРОЕКТЕ</label><textarea id="fMsg" placeholder="Хочу сайт, который плавится…"></textarea></div>
        <div><label>БЮДЖЕТ</label>
          <div class="budgets">
            <label><input type="radio" name="bud" checked><span>ДО 300K</span></label>
            <label><input type="radio" name="bud"><span>300K–1M</span></label>
            <label><input type="radio" name="bud"><span>1M+ ★</span></label>
          </div>
        </div>
        <button class="btn" type="submit" id="sendBtn">ЗАПУСТИТЬ ПРОЕКТ ★</button>
        <div class="form-ok" id="formOk">★ СИГНАЛ ПРИНЯТ ★<br>МЫ ОТВЕТИМ В ТЕЧЕНИЕ 24 ЧАСОВ.</div>
      </form>
    </div>
  </section>
</main>

<!-- FOOTER -->
<footer>
  <div class="wrap">
    <div class="foot__giant" aria-hidden="true">BST</div>
    <div class="foot__row">
      <span>© 2025 BST WORLDWIDE. ВСЕ ПРАВА РАСКАЛЕНЫ.</span>
      <div class="foot__soc"><a href="#">BE</a><a href="#">TG</a><a href="#">IG</a><a href="#">YT</a></div>
      <span>СДЕЛАНО НА ЧИСТОЙ КИСЛОТЕ ✦ РЕЖИМ: <b id="vNameFoot" style="color:var(--a1)">CORE</b></span>
    </div>
  </div>
</footer>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function y2kLandingEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
/* ================= BST ENGINE ================= */
const VARIANTS = {
  core : {name:'BST CORE',  tag:'★ DIGITAL HARDCORE <em>STUDIO</em> ★', sub:'Хром, кислота и пиксели. Мы собираем бренды и сайты, которые невозможно пролистать — интернет снова должен быть диким.', title:'BST® — DIGITAL HARDCORE STUDIO'},
  heat : {name:'BST HEAT',  tag:'★ FUEL FOR <em>BOLD</em> BRANDS ★',    sub:'Раскалённые айдентики и сайты, плавящие экраны. Осторожно: поверхность после нас остывает долго.', title:'BST® — FUEL FOR BOLD BRANDS'},
  vapor: {name:'BST VAPOR', tag:'★ DREAMS RENDERED <em>IN 4K</em> ★',   sub:'Неоновые сны из двухтысячных: бренды, которые пахнут жвачкой, озоном и VHS-плёнкой.', title:'BST® — DREAMS RENDERED IN 4K'},
  void : {name:'BST VOID',  tag:'★ BEYOND THE <em>INTERFACE</em> ★',    sub:'Интерфейсы с той стороны экрана. Глубокий космос, чистый хром, ноль компромиссов.', title:'BST® — BEYOND THE INTERFACE'}
};

/* ---- Loader ---- */
(function(){
  const fill=document.getElementById('lFill'),pct=document.getElementById('lPct'),ld=document.getElementById('loader');
  let p=0;const t=setInterval(()=>{p=Math.min(100,p+Math.random()*22);fill.style.width=p+'%';pct.textContent=Math.floor(p);
    if(p>=100){clearInterval(t);setTimeout(()=>ld.classList.add('done'),250);}},110);
})();

/* ---- Variant switcher ---- */
function setVariant(key){
  const v=VARIANTS[key];if(!v)return;
  document.body.dataset.variant=key;
  document.querySelectorAll('.sw').forEach(b=>b.classList.toggle('is-on',b.dataset.v===key));
  const tag=document.getElementById('heroTag');
  tag.innerHTML=v.tag;
  document.getElementById('heroSub').textContent=v.sub;
  document.getElementById('vName').textContent=v.name;
  document.getElementById('vNameFoot').textContent=key.toUpperCase();
  document.title=v.title;
  const f=document.getElementById('flash');f.classList.remove('on');void f.offsetWidth;f.classList.add('on');
  const ht=document.getElementById('heroTitle');ht.classList.add('glitching');setTimeout(()=>ht.classList.remove('glitching'),320);
}
document.querySelectorAll('.sw').forEach(b=>b.addEventListener('click',()=>setVariant(b.dataset.v)));
setVariant('${brand}');

/* ---- Burger menu ---- */
const burger=document.getElementById('burger');
burger.addEventListener('click',()=>{document.body.classList.toggle('menu-open');burger.textContent=document.body.classList.contains('menu-open')?'✕':'';});
document.querySelectorAll('.menu a.big').forEach(a=>a.addEventListener('click',()=>{document.body.classList.remove('menu-open');burger.textContent='≡';}));

/* ---- Random glitch on hero title ---- */
(function(){
  const t=document.getElementById('heroTitle');
  (function loop(){setTimeout(()=>{t.classList.add('glitching');setTimeout(()=>t.classList.remove('glitching'),300);loop();},2600+Math.random()*3200);})();
})();

/* ---- Reveal on scroll ---- */
const io=new IntersectionObserver(es=>es.forEach(e=>{if(e.isIntersecting){e.target.classList.add('in');io.unobserve(e.target);}}),{threshold:.15});
document.querySelectorAll('.rv').forEach(el=>io.observe(el));

/* ---- Count-up stats ---- */
const cio=new IntersectionObserver(es=>es.forEach(e=>{
  if(!e.isIntersecting)return;cio.unobserve(e.target);
  const el=e.target,end=+el.dataset.count,t0=performance.now();
  (function step(t){const k=Math.min(1,(t-t0)/1400),ease=1-Math.pow(1-k,3);
    el.textContent=Math.floor(end*ease);if(k<1)requestAnimationFrame(step);else el.textContent=end;})(t0);
}),{threshold:.6});
document.querySelectorAll('[data-count]').forEach(el=>cio.observe(el));

/* ---- Spark cursor (desktop only) ---- */
if(matchMedia('(pointer:fine)').matches){
  let last=0;
  addEventListener('pointermove',e=>{
    const now=performance.now();if(now-last<70)return;last=now;
    const s=document.createElement('span');s.className='spark';s.textContent='✦';
    s.style.left=(e.clientX+6)+'px';s.style.top=(e.clientY+6)+'px';
    s.style.color=['var(--a1)','var(--a2)','var(--a3)'][Math.floor(Math.random()*3)];
    document.body.appendChild(s);setTimeout(()=>s.remove(),700);
  });
}

/* ---- Form ---- */
document.getElementById('form').addEventListener('submit',e=>{
  e.preventDefault();
  const btn=document.getElementById('sendBtn');btn.textContent='ПЕРЕДАЧА…';btn.disabled=true;
  setTimeout(()=>{document.getElementById('formOk').classList.add('show');btn.textContent='ОТПРАВЛЕНО ✔';
    setTimeout(()=>{btn.textContent='ЗАПУСТИТЬ ПРОЕКТ ★';btn.disabled=false;},2200);},900);
});
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.rv,[data-rv],.reveal').forEach(function(el){el.classList.add('in');});
  document.querySelectorAll('.glitching').forEach(function(el){el.classList.remove('glitching');});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getY2kLandingPageHtml(data: Y2kLandingPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'core'
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST® — DIGITAL HARDCORE STUDIO</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/css/unbounded@latest/cyrillic-400-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/css/unbounded@latest/cyrillic-700-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/css/unbounded@latest/cyrillic-900-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/css/press-start-2p@latest/cyrillic-400-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/css/ibm-plex-mono@latest/cyrillic-400-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/css/ibm-plex-mono@latest/cyrillic-600-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/css/bungee@latest/latin-400-normal.css">
<style>${Y2K_LANDING_CSS}</style>
</head>
<body data-variant="${brand}">
${Y2K_LANDING_HTML_BODY}
<script>${y2kLandingEngineJs(brand, mode)}</script>
</body>
</html>`
}
